import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { palette, fontFamily } from '../../styles/tokens';

type Status = 'loading' | 'form' | 'success' | 'not_found' | 'disabled' | 'error';

const INPUT: React.CSSProperties = {
  width: '100%', padding: '14px 16px', borderRadius: 12, border: '1px solid #E5E5EA',
  fontSize: 17, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const,
  color: '#1A1A1A',
};

export function CheckInPage() {
  const { slug } = useParams<{ slug: string }>();
  const [status, setStatus] = useState<Status>('loading');
  const [businessName, setBusinessName] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resultMsg, setResultMsg] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [bookingTime, setBookingTime] = useState('');

  useEffect(() => {
    if (!slug) { setStatus('error'); return; }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- pre-existing; IIFE in effect
    (async () => {
      const { data: page } = await supabase.from('booking_pages').select('business_id, logo_url, businesses(name, enable_checkin)').eq('slug', slug).eq('is_active', true).maybeSingle();
      if (!page) { setStatus('error'); return; }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing; supabase joined-table type narrowing
      const biz = (page as any).businesses;
      setBusinessName(biz?.name ?? '');
      setLogoUrl(page.logo_url);
      if (!biz?.enable_checkin) { setStatus('disabled'); return; }
      setStatus('form');
    })();
  }, [slug]);

  const handleCheckIn = async () => {
    if (!name.trim() || !phone.trim()) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.rpc('client_checkin', {
        p_business_slug: slug, p_client_name: name.trim(), p_client_phone: phone.trim(),
      });
      if (error) throw error;
      if (data?.success) {
        setResultMsg(data.message ?? "You're checked in!");
        setServiceName(data.service_name ?? '');
        setBookingTime(data.booking_time ?? '');
        setStatus('success');
      } else if (data?.not_found) {
        setResultMsg(data.message ?? 'No booking found for today.');
        setStatus('not_found');
      } else {
        setResultMsg(data?.error ?? 'Something went wrong.');
        setStatus('error');
      }
    } catch { setStatus('error'); } finally { setSubmitting(false); }
  };

  const fmtTime = (t: string) => {
    const [hh, mm] = t.split(':').map(Number);
    return `${hh % 12 || 12}:${String(mm).padStart(2, '0')} ${hh >= 12 ? 'PM' : 'AM'}`;
  };

  const logo = logoUrl ?? 'https://crewbit.ca/logo.png';

  if (status === 'loading') return <Page logo={logo}><p style={{ color: '#636366' }}>Loading...</p></Page>;

  if (status === 'disabled') return (
    <Page logo={logo}>
      <h2 style={h2}>Welcome to {businessName}</h2>
      <p style={body}>Check-in isn't available right now. Please let our team know you've arrived.</p>
    </Page>
  );

  if (status === 'success') return (
    <Page logo={logo}>
      <div style={{ width: 64, height: 64, borderRadius: 20, margin: '0 auto 20px', background: `linear-gradient(135deg, ${palette.green}, #2E7D32)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
      </div>
      <h2 style={h2}>You're checked in!</h2>
      {serviceName && bookingTime && <p style={{ fontSize: 15, color: '#636366', margin: '0 0 8px' }}>Your {serviceName} at {fmtTime(bookingTime)}</p>}
      <p style={{ ...body, marginTop: 12 }}>{resultMsg}</p>
    </Page>
  );

  if (status === 'not_found') return (
    <Page logo={logo}>
      <h2 style={h2}>Welcome!</h2>
      <p style={body}>{resultMsg}</p>
      <button onClick={() => { setStatus('form'); setName(''); setPhone(''); }} style={linkBtn}>Try again</button>
    </Page>
  );

  if (status === 'error') return (
    <Page logo={logo}>
      <h2 style={h2}>Something went wrong</h2>
      <p style={body}>{resultMsg || 'Please let our team know you\'ve arrived.'}</p>
    </Page>
  );

  return (
    <Page logo={logo}>
      <h2 style={h2}>Check in for your appointment</h2>
      <p style={{ ...body, marginBottom: 24 }}>{businessName ? `Welcome to ${businessName}` : 'Enter your details to check in'}</p>

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 14, fontWeight: 500, color: '#636366', marginBottom: 6, display: 'block' }}>Name</label>
        <input style={INPUT} value={name} onChange={e => setName(e.target.value)} placeholder="Your name" autoFocus />
      </div>
      <div style={{ marginBottom: 24 }}>
        <label style={{ fontSize: 14, fontWeight: 500, color: '#636366', marginBottom: 6, display: 'block' }}>Phone number</label>
        <input style={INPUT} value={phone} onChange={e => setPhone(e.target.value)} placeholder="(555) 123-4567" type="tel" />
      </div>

      <button onClick={handleCheckIn} disabled={submitting || !name.trim() || !phone.trim()} style={{
        width: '100%', padding: '16px 0', borderRadius: 14, border: 'none',
        background: (!name.trim() || !phone.trim()) ? '#CCC' : palette.accent,
        color: '#fff', fontSize: 17, fontWeight: 600, fontFamily: 'inherit',
        cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1,
      }}>
        {submitting ? 'Checking in...' : 'Check In'}
      </button>
      {(!name.trim() || !phone.trim()) && !submitting && (
        <p style={{ fontSize: 13, color: '#8E8E93', marginTop: 10, marginBottom: 0, textAlign: 'center' }}>
          {!name.trim() && !phone.trim()
            ? 'Enter your name and phone number to continue'
            : !name.trim()
            ? 'Enter your name to continue'
            : 'Enter your phone number to continue'}
        </p>
      )}
    </Page>
  );
}

const h2: React.CSSProperties = { fontSize: 22, fontWeight: 700, color: '#1A1A1A', margin: '0 0 8px' };
const body: React.CSSProperties = { fontSize: 15, color: '#636366', lineHeight: 1.5, margin: 0 };
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', color: palette.accent, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginTop: 16, padding: 0 };

function Page({ logo, children }: { logo: string; children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', background: '#F5F5F5', fontFamily, padding: 24 }}>
      <div style={{ textAlign: 'center', padding: '36px 28px', background: '#fff', borderRadius: 20, maxWidth: 400, width: '100%', boxShadow: '0 2px 16px rgba(0,0,0,0.08)', marginTop: 48 }}>
        <img src={logo} alt="" height="32" style={{ marginBottom: 24 }} />
        {children}
      </div>
    </div>
  );
}
